<script setup lang="ts">
import { ChevronDownIcon } from '@heroicons/vue/24/solid';
import PropertyValue from '@/components/record/fields/PropertyValue.vue';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useInspectorSections } from '@/composables/useInspectorSections';
import type { InspectorFieldGroup } from '@/composables/searchInspectorFieldGroups';
import { computed, onMounted, provide, ref, type Component } from 'vue';
import { useElementSize } from '@vueuse/core';
import { stackSections } from '@/components/DataTable/stackSections';
import { SIDE_PEEK_BUTTON_FLOATS } from '@/composables/sidePeekButtonPlacement';
import { fieldKindFor, isEmptyFieldValue, propertyLabel } from '@/components/record/fields/fieldKind';
import type { TableColumnStaticProps } from '@/types/tableTypes';

/**
 * A field whose value is worth more than the text of the column. The component
 * receives the whole `row` and renders in place of the field's own display.
 *
 * `fullWidth` drops the label/value split and gives the component the row — for
 * values too big to sit beside a label, like a track card.
 */
export interface InspectorFieldOverride {
    component: Component;
    fullWidth?: boolean;
}

/**
 * A record's Details, as a properties panel (docs/specs/record-properties-panel.md).
 * Each section is a small heading over rows of a label and a value, with no
 * card border and no divider lines. Each value is drawn for its kind, such as
 * an avatar for a person or a dot for a status. An empty field keeps its place
 * and shows a dash, so a field is always where the layout puts it (Joel,
 * 2026-10-19). Open and closed sections are remembered per entity and section
 * ({@see useInspectorSections}).
 */
const props = withDefaults(
    defineProps<{
        row: Record<string, unknown>;
        groups: InspectorFieldGroup[];
        /** models.json key — scopes the remembered collapse state. */
        entityId: string;
        /** Field key => a component to render instead of the field's own display. */
        fieldOverrides?: Record<string, InspectorFieldOverride>;
    }>(),
    { fieldOverrides: () => ({}) }
);

const sections = useInspectorSections(() => props.entityId);

// A related record's side-peek button floats left of its name instead of
// pushing the name in from the label.
provide(SIDE_PEEK_BUTTON_FLOATS, true);

/**
 * True when a row shows a dash because the field has no value. A field the
 * inspector draws itself, such as the player's "Now playing" card, is never empty.
 */
function isEmptyRow(column: TableColumnStaticProps): boolean {
    return !props.fieldOverrides[column.key] && isEmptyFieldValue(props.row, column);
}

const groupsByTitle = computed(() => new Map(props.groups.map((group) => [group.title, group])));

function labelOf(column: TableColumnStaticProps): string {
    return propertyLabel(props.entityId, column, fieldKindFor(props.entityId, column));
}

// One column below 768 pixels of width, and two from 768, everywhere: the
// record page, the peeks and the split pane. Joel, 2026-10-17: two, like the
// other sections. Each section goes into the shorter column, so a section
// moves up under a collapsed one instead of waiting for the row beside it.
const TWO_COLUMN_MIN_WIDTH = 768;
const root = ref<HTMLElement | null>(null);
const { width: observedWidth } = useElementSize(root);
// The resize observer reports only after the first paint, and not at all while
// the page is hidden. Measuring once on mount means the first render already
// has the right number of columns.
const mountedWidth = ref(0);
onMounted(() => {
    mountedWidth.value = root.value?.getBoundingClientRect().width ?? 0;
});
const width = computed(() => observedWidth.value || mountedWidth.value);
const columnCount = computed(() => (width.value >= TWO_COLUMN_MIN_WIDTH ? 2 : 1));
const GRID_COLUMNS = ['grid-cols-1', 'grid-cols-2'] as const;
const stacks = computed(() =>
    stackSections(
        props.groups.map((group) => ({
            group,
            open: sections.isOpen(group.title),
            // Every field takes a row, empty or not.
            fieldCount: group.columns.length,
        })),
        columnCount.value,
    ),
);
</script>

<template>
    <!-- One column in a narrow peek, two where there is room, three on a wide
         record page. Each column is its own stack, so a section sits right
         under the one above it. -->
    <div
        ref="root"
        class="grid items-start gap-x-8 gap-y-6"
        :class="GRID_COLUMNS[stacks.length - 1]"
        data-testid="search-result-field-sections"
    >
      <div v-for="(stack, stackIndex) in stacks" :key="stackIndex" class="flex min-w-0 flex-col gap-6" data-testid="field-section-stack">
        <Collapsible
            v-for="group in stack"
            :key="group.title"
            :open="sections.isOpen(group.title)"
            :data-testid="`field-section-${group.title}`"
            @update:open="sections.setOpen(group.title, $event)"
        >
            <CollapsibleTrigger
                class="group -mx-1.5 flex items-center gap-1 rounded px-1.5 py-0.5 text-left outline-none hover:bg-muted focus-visible:ring-1 focus-visible:ring-ring"
            >
                <h4 class="text-xs font-semibold text-muted-foreground">
                    {{ group.title }}
                </h4>
                <ChevronDownIcon
                    class="size-3 shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=closed]:-rotate-90"
                    aria-hidden="true"
                />
            </CollapsibleTrigger>
            <CollapsibleContent>
                <dl class="mt-1 flex flex-col">
                    <template v-for="column in groupsByTitle.get(group.title)?.columns ?? []" :key="column.id">
                        <!-- Too big to sit beside a label: label on its own line,
                             then the component across the full width of the column. -->
                        <div
                            v-if="fieldOverrides[column.key]?.fullWidth"
                            class="flex flex-col gap-1 py-[7px] -mx-2 rounded-md px-2 transition-colors hover:bg-muted/50"
                            :data-testid="`field-row-${column.key}`"
                        >
                            <dt class="text-sm text-muted-foreground">{{ labelOf(column) }}</dt>
                            <dd class="min-w-0">
                                <component :is="fieldOverrides[column.key].component" :row="row" />
                            </dd>
                        </div>
                        <!-- The label on the left and the value against the right edge,
                             so the row uses the whole width of its column. On a phone
                             the label sits above the value. Hovering a row tints it, as
                             a table row is, so the eye can follow a label across a wide
                             gap to its value. -->
                        <div
                            v-else
                            class="group/field flex min-h-[34px] flex-col py-[7px] text-sm sm:flex-row sm:items-baseline sm:justify-between sm:gap-6 -mx-2 rounded-md px-2 transition-colors hover:bg-muted/50"
                            :data-testid="`field-row-${column.key}`"
                            :data-empty="isEmptyRow(column) ? 'true' : undefined"
                        >
                            <dt class="shrink-0 text-muted-foreground sm:max-w-[45%] [overflow-wrap:anywhere]">{{ labelOf(column) }}</dt>
                            <dd v-if="isEmptyRow(column)" class="text-muted-foreground/60 sm:text-right" aria-label="Empty">—</dd>
                            <dd v-else class="min-w-0 [overflow-wrap:anywhere] sm:flex sm:justify-end sm:text-right">
                                <component
                                    :is="fieldOverrides[column.key].component"
                                    v-if="fieldOverrides[column.key]"
                                    :row="row"
                                />
                                <PropertyValue v-else :entity-id="entityId" :column="column" :row="row" />
                            </dd>
                        </div>
                    </template>
                </dl>
            </CollapsibleContent>
        </Collapsible>
      </div>
    </div>
</template>
